// src/store/live/index.js
import merge from '@bundled-es-modules/deepmerge'
import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import { deleteProperty as delProp, setProperty as setProp } from 'dot-prop'
import { current, original, produce } from 'immer'
import { MAX_SAFE_INTEGER } from '~/lib/constants'
import { VECTOR_TYPES } from '~/lib/flipObject'
import { normalizeVectorScale } from '~/lib/normalizeVectorScale'
import { footprint, localExtent, localPivot } from '~/lib/objectBounds'
import {
	DROP,
	parseLivePath,
	quantize,
	reapSourceSettings,
	schemaForLivePath,
	settleSourceSettings,
	settleValue,
	sourceParamPath,
	sourceParams,
	sourceValues,
	writeSourceValues,
} from '~/lib/params'
import { isShaderSource } from '~/lib/shaders'
import { fillSourcesOf, isSourceFill } from '~/lib/sourceFill'
import { EFFECT_SCHEMAS, objectProps } from '~/lib/presetSettings'
import { conformEntry, conformModulators, detachFrom, linkInto, reapModulators, splitDef } from '~/lib/automation/model'
import { applyCarry } from '~/lib/livePins'
import { newObjectId } from '~/lib/objectIds'
import { anchorsOf } from '~/lib/outline/path'
import { rebindSegments } from '~/lib/outline/refs'
import { flipPathGeometry } from '~/lib/svg/paths'
import { capitalize, copy, generateName } from '~/lib/utils'
import initialState from './initial'
import { recursivelyDeleteObjects } from './utils'

const logger = console.scope('live-reducer', true)

const arrayMergeReplace = (d, s) => s

// A fill is one value: merged, a gradient and a source fill blend into an object that is both.
const replaceFill = key => (key === 'fill' ? (d, s) => s : undefined)

/**
 * The contract `initial` for a dot-path in `live`, or undefined if no schema declares it.
 *
 * Two shapes, one lookup: `motion.pan` is an effect param, `objects.<id>.x` an object param whose definition depends on the instance's own `type`.
 */
const propForPath = (s, path) => schemaForLivePath(path, s.objects)

// A fill switching one shader for another hands its loops to the new one for every id it declares: a loop is source-relative, as its controls are. Several shaders swapped at once are not paired.
const carryLoops = (s, before) => {
	if (!before || !s.modulators) return
	const after = fillSourcesOf(s.objects)
	const gone = [...before].filter(x => !after.has(x) && isShaderSource(x))
	const came = [...after].filter(x => !before.has(x) && isShaderSource(x))
	if (gone.length !== 1 || came.length !== 1) return
	const params = sourceParams(came[0])
	const from = sourceParamPath(gone[0], '')
	for (const path of Object.keys(s.modulators)) {
		if (!path.startsWith(from)) continue
		const key = path.slice(from.length)
		const to = sourceParamPath(came[0], key)
		if (params?.[key] && !s.modulators[to]) s.modulators[to] = s.modulators[path]
	}
}

// A fill change strands a source's bag entry and the loops on its params; `before` is the fill sources it changed from.
const reapFills = (s, before) => {
	carryLoops(s, before)
	reapSourceSettings(s)
	reapModulators(s)
}

const writeModulators = (s, section) => {
	const next = conformModulators(section, s.objects)
	if (next) s.modulators = next
	else delete s.modulators
}

/**
 * THE WRITE RULE: a value equal to its contract `initial` is DELETED, never stored.
 *
 * What makes absence trustworthy — without it `live` holds `{strength: 50}` where a pruned preset omits the key, and every preset reads permanently dirty the moment a user moves a slider and returns it. `updateObject` applies the same rule per key; this is the dot-path form.
 *
 * ⚠️ An UNDECLARED path is written, not dropped: no contract entry means no initial to compare, and silently discarding it would lose runtime keys no schema describes.
 */
const setPruned = (s, path, value) => {
	// ⚠️ AN `objects.*` PATH MAY ONLY WRITE TO AN OBJECT THAT EXISTS. `setProp` auto-vivifies every missing ancestor, so a pin outliving its object (a preset load that legitimately removed it) would RECREATE it as a bare `{prop}` fragment with no id/type, unreachable from `root.children` — a ghost that matches no preset, reads dirty forever, and re-arms itself on the next load.
	if (path.startsWith('objects.') && !s.objects?.[path.split('.')[1]]) return
	const p = parseLivePath(path)
	if (p?.scope === 'source') {
		// Only a source some fill uses keeps an entry.
		if (fillSourcesOf(s.objects).has(p.source))
			writeSourceValues(s, p.source, { ...sourceValues(s, p.source), [p.key]: value })
		return
	}
	const prop = propForPath(s, path)
	const v = settleValue(prop, value)
	if (v === DROP) {
		delProp(s, path)
		// ⚠️ EFFECTS ONLY — an emptied object is still a real object, and `objects` itself is never removed.
		const effect = path.slice(0, path.indexOf('.'))
		if (EFFECT_NAMES.has(effect) && s[effect] && !Object.keys(s[effect]).length) delete s[effect]
	} else setProp(s, path, v)
}

/** Bake a group's scale magnitude into a brush child's geometry, in place on the Immer draft. */
function bakeVectorScale(child, sx, sy) {
	const patch = normalizeVectorScale({ ...child, scaleX: sx, scaleY: sy })
	if (patch) {
		const { id: _id, ...geometry } = patch
		Object.assign(child, geometry)
	}
	// A vector type declares neither key, so the scale is DELETED rather than set to 1 — an undeclared param in `live` is one no reader can resolve through the contract.
	delete child.scaleX
	delete child.scaleY
}

/** The two keys a brush must never carry — its scale lives in `data`. */
const VECTOR_SCALE_KEYS = new Set(['scaleX', 'scaleY'])

/**
 * THE OBJECT WRITE RULE, applied per key to one instance on the Immer draft. The per-key form of `prune` (`~/lib/presetSettings`), which writes the same rule a whole section at a time for the effects.
 *
 * ⚠️ ONE FUNCTION FOR BOTH REDUCERS. `updateObjects` was a raw `Object.assign`, so every patch it carried skipped all three rules below — and it is the commit path for the whole transformer (`SVGTransformer`'s pointer-up dispatches it, not `updateObject`), which is where a signed `rotation - anc.rotation` and a baked `scaleX: 1` both enter. Two reducers writing the same shape must not hold two rules.
 */
const applyObjectPatch = (found, rest) => {
	const targetType = rest.type ?? found.type
	const props = objectProps(targetType)
	for (const k in rest) {
		const entry = props?.[k]
		// An explicit `undefined` is a DELETION, not a value: it is how a caller returns a key to its default without knowing what that default is.
		if (rest[k] === undefined) {
			delete found[k]
			continue
		}
		const value = settleValue(entry, rest[k])
		// Absence means AT INITIAL, for every param — no exemptions. Every reader resolves an absent key through the contract: `getObjectInitial` here, the generated per-type models in imagine_srv's rasterize.py.
		if (value === DROP) {
			delete found[k]
		} else if (!entry && VECTOR_SCALE_KEYS.has(k) && VECTOR_TYPES.has(targetType)) {
			// A brush carries no scale (it bakes) and declares none, so the generic branch would store a key no reader resolves.
			delete found[k]
		} else {
			found[k] = value
		}
	}
	if ('data' in rest && isSourceFill(found.fill) && found.fill.fit)
		found.fill = { ...found.fill, fit: rebindSegments(found.fill.fit, anchorsOf(found.data ?? '')) }
}

/** The write rule applied to an object mutated in place, for a reducer that accumulates rather than patches. Keys are snapshotted first: the loop deletes, and deleting during `for...in` over a draft is unspecified. */
const settleObject = obj => {
	const props = objectProps(obj.type)
	if (!props) return
	for (const k of Object.keys(obj)) {
		const entry = props[k]
		if (!entry) continue
		const value = settleValue(entry, obj[k])
		if (value === DROP) delete obj[k]
		else obj[k] = value
	}
}

// The effects a fast-path patch may name. Derived from the one map every direction iterates rather than hardcoded, so a new effect needs no edit here.
const EFFECT_NAMES = new Set(Object.keys(EFFECT_SCHEMAS))

/**
 * The write rule for a whole patch, on both `patchLiveObject` paths — see [presets.md](../../../context/presets.md) § The Write Rule. Only keys the PAYLOAD names are judged; `assign` also writes its values (the fast path), where the merge has already placed them.
 */
const applyEffectWriteRule = (s, payload, assign) => {
	for (const effect in payload) {
		const props = EFFECT_SCHEMAS[effect]?.properties
		const values = payload[effect]
		const section = s[effect]
		if (!section || typeof section !== 'object') continue
		if (!values || typeof values !== 'object' || Array.isArray(values)) continue
		for (const key in values) {
			const value = settleValue(props?.[key], assign ? values[key] : section[key])
			if (value === DROP) delete section[key]
			else if (assign || value !== section[key]) section[key] = value
		}
		if (!Object.keys(section).length) delete s[effect]
	}
}

/**
 * Can this patch skip the deep merge?
 *
 * A preset fade dispatches `patchLiveObject` on EVERY GSAP frame carrying only tweened effect-effect numbers. `deepmerge` is the wrong tool for that shape twice over: it READS every node of the Immer draft — materializing a proxy per node, so a scene's whole `objects` map is walked each frame — and it RETURNS A PLAIN OBJECT, which discards structural sharing so every effect and every object gets a fresh reference and no `createSelector` downstream can memoize. Measured on a 200-object scene: 3.3ms/frame vs 0.002ms, and the deepmerge cost scales with scene size while the assign does not.
 *
 * A WHITELIST, deliberately: anything this does not fully recognize takes the merge below and keeps today's exact behavior. All-or-nothing — one unrecognized key sends the whole payload down the slow path, so there is no partial application to reason about.
 */
const isEffectPatch = (s, payload) => {
	for (const effect in payload) {
		// Excludes `objects` and `effects` — both need real recursion (and `objects` carries null deletion sentinels that cleanupMergedObjects below must see).
		if (!EFFECT_NAMES.has(effect)) return false
		const values = payload[effect]
		if (!values || typeof values !== 'object' || Array.isArray(values)) return false
		// An effect absent from state has no branch to assign onto — deepmerge creates it, `Object.assign(undefined, …)` would throw. Reachable since effects can be removed, and the deepmerge path is how presence arrives from a peer.
		if (!s[effect] || typeof s[effect] !== 'object') return false
		for (const key in values) {
			const value = values[key]
			// A nested sub-dict (`diffusion.advanced`) must deep-merge: a shallow assign would replace the whole dict and wipe the sibling keys a partial diff did not mention. A morph envelope is the opposite case — it is a VALUE, replaced wholesale every tick by design.
			//
			// ⚠️ Asks the CONTRACT whether the param is morphable, not `isMorphEnvelope` — that test is "is this an object", because the wire type is `T | MorphEnvelope` and completeness belongs to the server's validator. Applied here it would call `advanced` an envelope and take the fast path, shallow-assigning the whole sub-dict and wiping exactly the sibling keys this guard exists to protect.
			if (value && typeof value === 'object' && !EFFECT_SCHEMAS[effect]?.properties?.[key]?.morph) return false
		}
	}
	return true
}

// Post-merge cleanup: process null deletion sentinels and remove dangling children refs
const cleanupMergedObjects = (s, payload) => {
	if (!payload.objects) return
	// Handle null deletion sentinels from the wire
	for (const key in payload.objects) {
		if (payload.objects[key] === null) {
			delete s.objects[key]
		} else if (s.objects[key]) settleObject(s.objects[key])
	}
	// Remove corrupt objects missing required structural fields (e.g. from dropped creation diffs)
	for (const id in s.objects) {
		const obj = s.objects[id]
		if (obj && id !== 'root' && (!obj.id || !obj.type)) {
			logger.warn('cleanupMergedObjects: removing corrupt object missing id/type', id, obj)
			delete s.objects[id]
			for (const pid in s.objects) {
				const parent = s.objects[pid]
				if (parent?.children) {
					parent.children = parent.children.filter(c => c !== id)
				}
			}
		}
	}
	// Integrity check: remove dangling children references
	for (const id in s.objects) {
		const obj = s.objects[id]
		if (obj?.children) {
			obj.children = obj.children.filter(childId => {
				if (!s.objects[childId]) {
					logger.warn('cleanupMergedObjects: removing dangling child ref', childId, 'from', id)
					return false
				}
				return true
			})
		}
	}
	// Remove orphaned objects: in the map but not reachable from root via children traversal.
	const reachable = new Set(['root'])
	const markReachable = id => {
		if (reachable.has(id)) return
		reachable.add(id)
		const obj = s.objects[id]
		if (obj?.children) obj.children.forEach(markReachable)
	}
	if (s.objects['root']?.children) s.objects['root'].children.forEach(markReachable)
	for (const id in s.objects) {
		if (!reachable.has(id)) {
			logger.warn('cleanupMergedObjects: removing orphan', id)
			delete s.objects[id]
		}
	}
	reapModulators(s)
}

/** `setLiveObject`'s payload with its objects cleaned, carried and reachable from root, and its modulators conformed to them. */
const settledObjects = (payload, meta) => {
	if (!payload.objects) return payload
	const newObjects = {}
	for (const key in payload.objects) {
		const obj = payload.objects[key]
		if (obj && typeof obj === 'object' && key !== 'root' && (!obj.id || !obj.type)) {
			logger.warn('setLiveObject: removing corrupt object missing id/type', key, obj)
			continue
		}
		// TEMP: the `text` type is disabled during the UI migration; legacy text objects are dropped on load.
		if (obj && obj.type === 'text') {
			continue
		}
		newObjects[key] = obj
	}
	// TEMP (paired with the text-drop guard above): scrub `children` refs that point at a dropped object (e.g. a legacy text child of a group/root), so no parent keeps a dangling id. Remove when the text type is reintegrated.
	for (const id in newObjects) {
		const obj = newObjects[id]
		if (obj?.children?.length) {
			const filtered = obj.children.filter(cid => cid === 'root' || newObjects[cid])
			if (filtered.length !== obj.children.length) {
				newObjects[id] = { ...obj, children: filtered }
			}
		}
	}
	// ⚠️ BEFORE the reachability sweep, which is the whole point: a pinned object the preset lacks is unreachable until the carry re-attaches it.
	applyCarry(newObjects, meta?.carryObjects)
	// Remove orphaned objects: in the map but not reachable from root via children traversal.
	const reachable = new Set(['root'])
	const markReachable = id => {
		if (reachable.has(id)) return
		reachable.add(id)
		const obj = newObjects[id]
		if (obj?.children) obj.children.forEach(markReachable)
	}
	if (newObjects['root']?.children) newObjects['root'].children.forEach(markReachable)
	for (const id in newObjects) {
		if (!reachable.has(id)) {
			logger.warn('setLiveObject: removing orphan', id)
			delete newObjects[id]
		}
	}
	const next = { ...payload, objects: newObjects }
	const modulators = conformModulators(payload.modulators, newObjects)
	if (modulators) next.modulators = modulators
	else delete next.modulators
	const sourceSettings = settleSourceSettings(payload.source_settings, newObjects)
	if (sourceSettings) next.source_settings = sourceSettings
	else delete next.source_settings
	return next
}

const liveSlice = createSlice({
	name: 'live',
	initialState,
	reducers: {
		setLiveObject: (s, { payload, meta }) => {
			// The server's state never carries the bag: a phone keeps the one the runner sent.
			const next = settledObjects(
				meta?.sync && !payload.source_settings && s.source_settings
					? { ...payload, source_settings: s.source_settings }
					: payload,
				meta
			)
			// ⚠️ Pins land in THIS action: a separate restore puts the preset's value on the wire first.
			return meta?.pins?.length
				? produce(next, d => meta.pins.forEach(([path, v]) => setPruned(d, path, v)))
				: next
		},
		patchLiveObject: (s, { payload }) => {
			// Fast path for the transition's per-frame dispatch — see isEffectPatch. Mutates the draft and returns NOTHING: returning a value replaces the whole state, which is exactly the structural sharing this exists to preserve.
			//
			// ⚠️ Assigns THROUGH THE WRITE RULE (`setPruned` above), never raw. A tween bag is DENSE — it materializes every animated key, resolving an absent endpoint to its contract `initial` because GSAP cannot tween from `undefined` — so a raw assign writes back exactly the at-initial keys the sparse model deletes. A completed transition hides it (the final `setLiveObject` replaces `live` with the sparse preset), but an INTERRUPTED one leaves them stranded, and `selectPresetChanged` diffs on key existence: the preset reads permanently dirty and no amount of saving clears it, since `stripSettings` keeps pruning the stored side sparse.
			if (isEffectPatch(s, payload)) {
				applyEffectWriteRule(s, payload, true)
				return
			}
			s = merge(s, payload, { arrayMerge: arrayMergeReplace, customMerge: replaceFill })
			// ⚠️ The container-level delete sentinel, at the EFFECT level. `deepmerge` treats a null as a VALUE, so without this a peer's removal lands as `live.control === null` — present to every `in` test, broken to every reader. Same rule `cleanupMergedObjects` applies per object instance.
			for (const effect in payload) if (payload[effect] === null && EFFECT_NAMES.has(effect)) delete s[effect]
			// ⚠️ The SAME write rule — `deepmerge` has already assigned raw, so this only prunes what it stranded.
			applyEffectWriteRule(s, payload, false)
			cleanupMergedObjects(s, payload)
			return s
		},
		resetLiveObject: () => {
			// Object.assign(s, initialState)
			return copy(initialState)
		},
		addObjects: (s, { payload }) => {
			const { objects } = s

			const ids = []
			// A given id is kept (a pasted object is the same object); one already taken here is re-minted, and the batch's references follow it.
			const renamed = new Map()
			const reg = /^(.+)(\d+)$/
			const addSingleObject = obj => {
				let { id, type, name, parentId, ...object } = obj
				const m = name?.match(reg)
				const prefix = m ? m[1] : `${capitalize(type)} `
				name = generateName(objects, 'name', name, prefix)
				if (type === 'rect') type = 'path'
				const fresh = id && !objects[id] ? id : newObjectId(type, objects)
				if (id && fresh !== id) renamed.set(id, fresh)
				id = fresh

				parentId = renamed.get(parentId) ?? parentId
				if (!parentId) parentId = 'root'
				let parent = objects[parentId]
				/* A preserved parentId can legitimately not exist here — pasting into another project, or after the source parent was deleted. Throwing from inside a reducer leaves the store mid-update and kills the paste with no feedback, so re-home the object instead. */
				if (!parent) {
					logger.warn('addObjects', 'Parent not found, re-parenting to root', parentId, objects)
					parentId = 'root'
					parent = objects.root
				}

				if (!parent.children) parent.children = []
				parent.children.push(id)

				/* Identity only, then THE WRITE RULE: callers pass tool state verbatim (an unset `current_stroke` is `''`, its initial), and a key stored at initial reads permanently dirty — save keeps it, the cache's `conform` prunes it. */
				object = { id, type, name, parentId, ...object }
				objects[id] = object
				settleObject(objects[id])

				ids.push(id)
			}

			if (Array.isArray(payload)) {
				payload.forEach(addSingleObject)
			} else {
				addSingleObject(payload)
			}
			for (const id of ids)
				for (const k of ['mask', 'maskFor'])
					if (renamed.has(objects[id][k])) objects[id][k] = renamed.get(objects[id][k])

			// Dispatch action to editor slice to set selected object ids This will be handled by a thunk later
		},
		updateObject: (s, { payload }) => {
			const { id, ...rest } = payload
			const { objects } = s
			const found = objects[id]
			if (!found) {
				logger.warn('updateObject', 'Object not found', { id, objects: original(objects) })
				return
			}

			if ('parentId' in rest && rest.parentId !== found.parentId) {
				let parent = objects[found.parentId]
				parent.children = parent.children.filter(f => f !== id)
				parent = objects[rest.parentId]
				parent.children.push(id)
			}

			const before = 'fill' in rest ? fillSourcesOf(s.objects) : null
			applyObjectPatch(found, rest)
			if (before) reapFills(s, before)
		},
		updateObjects: (s, { payload }) => {
			const { objects } = s
			const before = payload.some(o => 'fill' in o) ? fillSourcesOf(objects) : null
			payload.forEach(obj => {
				const found = objects[obj.id]
				if (found) {
					const { id: _id, ...rest } = obj
					applyObjectPatch(found, rest)
				} else {
					logger.warn('updateObjects', 'Object not found', obj.id)
				}
			})
			if (before) reapFills(s, before)
		},
		groupObjects: (s, { payload: selectedIds }) => {
			if (!selectedIds || selectedIds.length < 2) {
				logger.warn('groupObjects', 'Not enough objects selected', selectedIds)
				return
			}

			const { objects } = s

			// All selected must share the same parent
			const commonParent = objects[selectedIds[0]]?.parentId
			if (!commonParent || selectedIds.some(id => objects[id]?.parentId !== commonParent)) {
				logger.warn('groupObjects', 'Cannot group: objects must share the same parent')
				return
			}

			const groupId = newObjectId('group', objects)

			// The group's pivot is the centre of its members' footprints; its local origin IS that pivot, so the members are re-expressed about it.
			let minX = Infinity,
				minY = Infinity,
				maxX = -Infinity,
				maxY = -Infinity
			const parentId = objects[selectedIds[0]]?.parentId || 'root'
			const parent = objects[parentId]
			if (!parent) return
			const groupedObjects = []

			const len = parent.children.length
			for (let i = 0; i < len; i++) {
				const id = parent.children[i]
				if (!selectedIds.includes(id)) continue
				const obj = objects[id]
				for (const p of footprint(obj, objects)) {
					if (p.x < minX) minX = p.x
					if (p.x > maxX) maxX = p.x
					if (p.y < minY) minY = p.y
					if (p.y > maxY) maxY = p.y
				}
				groupedObjects.push(obj)
			}
			const cx = Number.isFinite(minX) ? (minX + maxX) / 2 : 0
			const cy = Number.isFinite(minY) ? (minY + maxY) / 2 : 0

			// update objects positions and parentId, remove from parent's children
			groupedObjects.forEach(obj => {
				obj.x = (obj.x ?? 0) - cx
				obj.y = (obj.y ?? 0) - cy
				parent.children = parent.children.filter(f => f !== obj.id)
				obj.parentId = groupId
			})

			const group = {
				id: groupId,
				type: 'group',
				name: generateName(objects, 'name', '', 'Group '),
				parentId,
				x: cx,
				y: cy,
				scaleX: 1,
				scaleY: 1,
				rotation: 0,
				children: groupedObjects.map(obj => obj.id),
			}
			parent.children.push(groupId)

			s.objects[groupId] = group
		},
		unGroupObjects: (s, { payload: selectedIds }) => {
			const { objects } = s

			if (!selectedIds?.length) {
				logger.warn('unGroupObjects', 'No group ids provided')
				return
			}

			const groupIdsSet = new Set(selectedIds)

			if (!groupIdsSet.size) {
				logger.warn('unGroupObjects', 'No groups selected')
				return
			}

			const ungroupedIds = []

			let x1, y1, x2, y2, rot
			selectedIds.forEach(groupId => {
				const group = objects[groupId]
				const { parentId } = group
				const parent = objects[parentId]
				const sx = group.scaleX ?? 1
				const sy = group.scaleY ?? 1
				group.children.forEach(childId => {
					const child = objects[childId]
					x1 = child.x || 0
					y1 = child.y || 0
					rot = (group.rotation * Math.PI) / 180 || 0
					x2 = x1 * sx * Math.cos(rot) - y1 * sy * Math.sin(rot)
					y2 = x1 * sx * Math.sin(rot) + y1 * sy * Math.cos(rot)
					child.x = x2 + group.x
					child.y = y2 + group.y
					if (child.type === 'image') {
						// No position correction: the sprite mirrors in place about its anchor, which is the pivot `child.x`/`y` already name.
						// Size lives in width/height for these types, so the group's MAGNITUDE goes there — matching the vector branch. Only the SIGN goes to the scale, where it is the mirror. Multiplying both would scale the child twice (the old code did neither: it scaled `scaleX`, which nothing rendered).
						child.width = (child.width || 0) * Math.abs(sx)
						child.height = (child.height || 0) * Math.abs(sy)
						if (sx < 0) child.scaleX = -(child.scaleX ?? 1)
						if (sy < 0) child.scaleY = -(child.scaleY ?? 1)
					} else if (child.type === 'brush' && child.data && (sx < 0 || sy < 0)) {
						// A brush flips its geometry when inheriting a negative scale.
						const flipA = sx < 0 ? -1 : 1
						const flipD = sy < 0 ? -1 : 1
						const oldExtent = localExtent(child)
						const oldPivot = localPivot(child, null, oldExtent)
						const flipped = flipPathGeometry(child, flipA, flipD)
						child.data = flipped.data
						if (flipped.centerline) child.centerline = flipped.centerline
						// The flip mirrors the data across its extent; the group's mirror turned it about the child's pivot. The two agree at a centred pivot and differ by `(1 − 2a)·w` otherwise — in the child's local (pre-rotation) frame, pushed to world by R(groupRot)·diag(sx,sy)·R(childRot).
						const childRot = ((child.rotation || 0) * Math.PI) / 180
						const cCos = Math.cos(childRot),
							cSin = Math.sin(childRot)
						const gCos = Math.cos(rot),
							gSin = Math.sin(rot)
						const dlx = sx < 0 ? oldExtent.width - 2 * (oldPivot.x - oldExtent.x) : 0
						const dly = sy < 0 ? oldExtent.height - 2 * (oldPivot.y - oldExtent.y) : 0
						const crx = dlx * cCos - dly * cSin
						const cry = dlx * cSin + dly * cCos
						const wx = crx * sx * gCos - cry * sy * gSin
						const wy = crx * sx * gSin + cry * sy * gCos
						child.x += wx
						child.y += wy
						if (flipped.rawPoints != null) {
							// Local data → world: the data point at the pivot sits on `x`/`y`.
							const pv = localPivot(child)
							const rp = flipped.rawPoints.split(',').map(Number)
							for (let k = 0; k < rp.length; k += 2) {
								rp[k] += child.x - pv.x
								rp[k + 1] += child.y - pv.y
							}
							child.rawPoints = rp.join(',')
						}
						const newExtent = localExtent(child)
						child.width = newExtent.width
						child.height = newExtent.height
						// The flip baked the SIGN into `data`; the magnitude is baked here.
						bakeVectorScale(child, Math.abs(sx), Math.abs(sy))
					} else if (child.type === 'brush') {
						bakeVectorScale(child, sx, sy)
					} else {
						child.scaleX = (child.scaleX ?? 1) * sx
						child.scaleY = (child.scaleY ?? 1) * sy
					}
					child.rotation = (child.rotation || 0) + (group.rotation || 0)
					// ⚠️ Settled ONCE, on the finished values: this reducer accumulates in place (`child.x` is written, added to, then read back for `rawPoints`), so rounding per assignment would compound into the next `+=`.
					settleObject(child)

					child.parentId = parentId
					parent.children.push(childId)
					ungroupedIds.push(childId)
				})
				delete group.children
			})

			recursivelyDeleteObjects(s, groupIdsSet)
		},
		sortObjects: (s, { payload }) => {
			const { draggedIds, targetId, mode } = payload
			const draggedId = draggedIds[0]
			if (draggedId === targetId) return
			const { objects } = s

			const targetObj = objects[targetId]
			if (!targetObj) {
				logger.warn('sortObjects', 'Invalid drag/drop: target not found')
				return
			}

			// Validate if dropping into a non-group
			if (mode === 'into' && targetObj.type !== 'group') {
				logger.warn('sortObjects', 'Invalid drag/drop: can only drop into groups')
				return
			}

			// Prevent dropping into descendants
			const isDescendant = childId => {
				if (draggedIds.includes(childId)) return true
				const child = objects[childId]
				if (!child) return false
				return isDescendant(child.parentId)
			}

			if (mode === 'into' && isDescendant(targetId)) {
				logger.warn('sortObjects', 'Invalid drag/drop: cannot drop into descendant', draggedId, targetId)
				return
			}

			// Remove all dragged items from source parent
			const sourceParent = objects[objects[draggedId].parentId]
			sourceParent.children = sourceParent.children.filter(id => !draggedIds.includes(id))

			// Get source and destination parents
			const destParent = mode === 'into' ? targetObj : objects[targetObj.parentId]

			if (sourceParent !== destParent) {
				const s = { x: sourceParent.x || 0, y: sourceParent.y || 0, r: sourceParent.rotation || 0 }
				const d = { x: destParent.x || 0, y: destParent.y || 0, r: destParent.rotation || 0 }
				let obj, x1, y1, rot
				draggedIds.forEach(id => {
					obj = objects[id]
					x1 = (obj.x || 0) - d.x
					y1 = (obj.y || 0) - d.y
					rot = (s.r - d.r) * (Math.PI / 180)
					obj.x = x1 * Math.cos(rot) - y1 * Math.sin(rot) + s.x
					obj.y = x1 * Math.sin(rot) + y1 * Math.cos(rot) + s.y
					obj.rotation = (obj.rotation || 0) + (s.r - d.r)
					settleObject(obj)
				})
			}

			// Add to destination parent
			if (mode === 'into') {
				destParent.children.push(...draggedIds)
			} else {
				const targetIndex = destParent.children.indexOf(targetId)
				if (targetIndex === -1) {
					logger.warn('sortObjects', 'Invalid drag/drop: target index not found', {
						parent: current(destParent),
						targetId,
						draggedId,
					})
					return
				}
				const insertIndex = mode === 'after' ? targetIndex : targetIndex + 1
				destParent.children.splice(insertIndex, 0, ...draggedIds)
			}

			if (!sourceParent.children.length) {
				recursivelyDeleteObjects(s, [sourceParent.id])
			}

			// Update parentIds of dragged objects
			draggedIds.forEach(id => {
				objects[id].parentId = destParent.id
			})
		},
		/**
		 * A mask is a TWO-WAY link: `target.mask === maskId` and `objects[maskId].maskFor === targetId`. Both halves are written here and both are removed by `recursivelyDeleteObjects`.
		 *
		 * ⚠️ UNMASKING DELETES THE KEY rather than assigning a falsy id. `target.mask = undefined` leaves the key present with no partner — the one-way state `assertMaskLinks` crashes on, authored by the very reducer that is supposed to keep the pair whole.
		 */
		setObjectMask: (s, { payload }) => {
			const { targetId, maskId } = payload
			if (targetId === maskId) return
			const { objects } = s
			const target = objects[targetId]
			if (!target) return
			// The outgoing partner may already be gone (deleted in the same tick); dropping our half is still correct.
			if (target.mask && objects[target.mask]) delete objects[target.mask].maskFor
			if (maskId?.length) {
				// Naming a mask that does not exist would write exactly the half-link this pairing forbids.
				if (!objects[maskId]) return
				target.mask = maskId
				objects[maskId].maskFor = targetId
			} else {
				delete target.mask
			}
		},
		deleteObjects: (s, { payload: ids }) => {
			logger.info('deleteObjects', ids)
			recursivelyDeleteObjects(s, ids)
		},
		bringObjectToFront: (s, { payload: selectedIds }) => {
			const { objects } = s
			const selected = selectedIds || []
			if (!selected.length) {
				logger.warn('bringObjectToFront', 'No objects selected or provided')
				return
			}
			const parent = objects[objects[selected[0]].parentId]
			const children = parent.children
			const len = children.length
			const index = children.indexOf(selected[0])
			if (index === len - 1) return
			children.splice(index, selected.length)
			children.push(...selected)
		},
		sendObjectToBack: (s, { payload: selectedIds }) => {
			const { objects } = s
			const selected = selectedIds || []
			if (!selected.length) {
				logger.warn('sendObjectToBack', 'No objects selected or provided')
				return
			}
			const parent = objects[objects[selected[0]].parentId]
			const children = parent.children
			const index = children.indexOf(selected[0])
			if (index === 0) return
			children.splice(index, selected.length)
			children.unshift(...selected)
		},
		/** `{ ids, fill, values? }`: each shape's fill becomes `fill`, and a source fill's bag takes `values` over its own, one step. With `object`, the shape is added at root first under `ids[0]`. */
		applySourceFill: (s, { payload: { ids, object, fill, values } }) => {
			const [first] = ids
			if (object && !s.objects[first]) {
				const name = generateName(s.objects, 'name', object.name, `${capitalize(object.type)} `)
				s.objects[first] = { ...object, id: first, name, parentId: 'root' }
				s.objects.root.children.push(first)
				settleObject(s.objects[first])
			}
			const found = ids.map(id => s.objects[id]).filter(Boolean)
			if (!found.length) return
			const before = fillSourcesOf(s.objects)
			for (const obj of found) applyObjectPatch(obj, { fill })
			reapFills(s, before)
			if (values && isSourceFill(fill))
				writeSourceValues(s, fill.source, { ...sourceValues(s, fill.source), ...values })
		},
		/** The runner's whole bag, on a phone: the server's feed never carries it. */
		receiveSourceSettings: (s, { payload }) => {
			const bags = settleSourceSettings(payload, s.objects)
			if (bags) s.source_settings = bags
			else delete s.source_settings
		},
		/** `{ source, values }`: that source's whole entry, settled; ignored for a source no fill uses. */
		setSourceValues: (s, { payload: { source, values } }) => {
			if (fillSourcesOf(s.objects).has(source)) writeSourceValues(s, source, values)
		},
		setLiveProperty: (s, { payload }) => {
			const [k, v] = payload
			setPruned(s, k, v)
		},
		setLiveProperties: (s, { payload }) => {
			// payload: [['motion.pan', 50], ['motion.tilt', 60]]
			for (const [k, v] of payload) {
				setPruned(s, k, v)
			}
		},
		/** `{ path, def }`: create or replace the loop on `path`; on a shared drive, `def`'s motion replaces the driver's. A def that cannot apply removes it. */
		setModulator: (s, { payload: { path, def } }) => {
			const src = s.modulators?.[path]?.src
			if (!src || !def) return writeModulators(s, { ...s.modulators, [path]: def })
			const { motion, mapping } = splitDef(def)
			writeModulators(s, { ...s.modulators, [src]: motion, [path]: { src, ...mapping } })
		},
		/** `{ path, patch, value? }`: merge into an existing loop — its motion into a shared drive's driver; `value` becomes the param's number in the same step (a drive handing back to the hand). Turning a shared drive off detaches `path` from it. */
		updateModulator: (s, { payload: { path, patch, value } }) => {
			const current = s.modulators?.[path]
			if (!current) return
			if (!current.src) {
				const def = conformEntry(path, { ...current, ...patch }, s.objects)
				if (def) s.modulators[path] = def
			} else if (patch.drive === 'manual') writeModulators(s, detachFrom(s.modulators, path, patch))
			else {
				const { motion, mapping } = splitDef(patch)
				const driver = conformEntry(current.src, { ...s.modulators[current.src], ...motion }, s.objects)
				const link = conformEntry(path, { ...current, ...mapping }, s.objects)
				if (driver && link) {
					s.modulators[current.src] = driver
					s.modulators[path] = link
				}
			}
			if (value !== undefined) setPruned(s, path, value)
		},
		/** `{ path, from, mapping }`: drive `path` by `from`'s drive, `mapping` (its in/out) its own; `from: null` detaches it with a copy of the drive. */
		linkModulator: (s, { payload: { path, from, mapping } }) => {
			writeModulators(s, from ? linkInto(s.modulators, path, from, mapping) : detachFrom(s.modulators, path))
		},
		/** A peer's `modulators` patch, `{ path: def | null }`; `null` drops that loop. */
		patchModulators: (s, { payload }) => {
			const next = { ...s.modulators }
			for (const path in payload) {
				if (payload[path] === null) delete next[path]
				else next[path] = payload[path]
			}
			writeModulators(s, next)
		},
		// AddEffect and removeEffect not implemented //
	},
})

export const {
	setLiveObject,
	patchLiveObject,
	resetLiveObject,
	addObjects,
	deleteObjects,
	updateObject,
	updateObjects,
	sortObjects,
	bringObjectToFront,
	sendObjectToBack,
	groupObjects,
	unGroupObjects,
	setObjectMask,
	applySourceFill,
	receiveSourceSettings,
	setSourceValues,
	setLiveProperty,
	setLiveProperties,
	setModulator,
	updateModulator,
	linkModulator,
	patchModulators,
} = liveSlice.actions

/** Every action that writes `live`. Lives here, not in a middleware, so a consumer matching on it need not import the wire layer. */
export const liveActions = isAnyOf(
	setLiveObject,
	resetLiveObject,
	patchLiveObject,
	setLiveProperty,
	setLiveProperties,
	addObjects,
	updateObject,
	updateObjects,
	setObjectMask,
	applySourceFill,
	setSourceValues,
	sortObjects,
	deleteObjects,
	setModulator,
	updateModulator,
	linkModulator,
	patchModulators
)

// Export the reducer
export default liveSlice.reducer
